import _ from "lodash";
import { LngLat } from "mapbox-gl";
import { makeAutoObservable } from "mobx";
import { MapRef } from "react-map-gl";
import IParcel from "../interfaces/IParcel";
import IProperty from "../interfaces/IProperty";
import GeometryService from "../services/geometryService";
import MapboxApiService from "../services/mapboxApiService";
import ParcelApiService from "../services/parcelApiService";
import { buildProperty } from "../utils/propertyUtils";

export default class PropertyStore {
    isLoadingFor: Record<string, boolean> = {};
    isErrorFor: Record<string, boolean> = {};
    parcels = new Map<string, IParcel>();
    properties = new Map<string, IProperty>();

    constructor(
        private parcelApiService: ParcelApiService,
        private mapboxApiService: MapboxApiService,
        private geometryService: GeometryService,
    ) {
        makeAutoObservable(this);
    }

    setIsLoadingFor(key: string, value: boolean) {
        this.isLoadingFor[key] = value;
    }

    setIsErrorFor(key: string, value: boolean) {
        this.isErrorFor[key] = value;
    }

    getIsLoading(coordinate: LngLat | undefined): boolean {
        return coordinate ? (this.isLoadingFor[this.propertyKey(coordinate)] ?? false) : false;
    }

    getIsError(coordinate: LngLat | undefined): boolean {
        return coordinate ? (this.isErrorFor[this.propertyKey(coordinate)] ?? false) : false;
    }

    getProperty(coordinate: LngLat | undefined): IProperty | undefined {
        if (!coordinate)
            return undefined;

        return this.properties.get(this.propertyKey(coordinate));
    }

    async fetchProperty(coordinate: LngLat, map: MapRef): Promise<void> {
        const key = this.propertyKey(coordinate);

        if (this.properties.has(key))
            return;

        this.isErrorFor[key] = false;
        this.isLoadingFor[key] = true;

        try {
            const parcel = this.findParcel(coordinate) ?? (await this.fetchParcels([coordinate], map))[0];

            this.properties.set(key, buildProperty([parcel]));
        } catch {
            this.setIsErrorFor(key, true);
        } finally {
            this.setIsLoadingFor(key, false);
        }
    }

    async addParcelsToProperty(propertyCoordinate: LngLat, parcelCoordinates: LngLat[], map: MapRef): Promise<void> {
        const key = this.propertyKey(propertyCoordinate);
        const currentProperty = this.properties.get(key);
        if (!currentProperty)
            throw new Error("Property not found.");

        this.isErrorFor[key] = false;
        this.isLoadingFor[key] = true;

        try {
            const newCoordinates = parcelCoordinates.filter(coord => !this.findParcel(coord));
            if (newCoordinates.length)
                await this.fetchParcels(newCoordinates, map);
        } catch {
            this.setIsErrorFor(key, true);
            return;
        } finally {
            this.setIsLoadingFor(key, false);
        }

        const existingIds = new Set(currentProperty.parcelIds);
        const newParcelIds = _.uniq(parcelCoordinates
            .map(coord => this.findParcel(coord)!)
            .filter(p => !existingIds.has(p.id))
            .map(p => p.id));

        if (!newParcelIds.length)
            return;

        const parcels = [...currentProperty.parcelIds, ...newParcelIds]
            .map(id => this.parcels.get(id)!);

        this.properties.set(key, buildProperty(parcels, currentProperty.baseParcelId));
    }

    removeParcelFromProperty(coordinate: LngLat, parcelId: string) {
        const key = this.propertyKey(coordinate);
        const currentProperty = this.properties.get(key);
        if (!currentProperty)
            throw new Error("Property not found.");

        const parcels = currentProperty.parcelIds
            .filter(id => id !== parcelId)
            .map(id => this.parcels.get(id)!);

        if (!parcels.length)
            throw new Error("Cannot remove the last parcel from property.");

        this.properties.set(key, buildProperty(parcels, currentProperty.baseParcelId));
    }

    findParcelAtCoordinate(propertyCoordinate: LngLat, point: LngLat): IParcel | undefined {
        const property = this.properties.get(this.propertyKey(propertyCoordinate));
        if (!property)
            return undefined;

        const geoPoint = { latitude: point.lat, longitude: point.lng };
        return property.parcelIds
            .map(id => this.parcels.get(id)!)
            .find(parcel => this.geometryService.isPointInsideShape(geoPoint, parcel.shape));
    }

    clear() {
        this.parcels.clear();
        this.properties.clear();
        this.isLoadingFor = {};
        this.isErrorFor = {};
    }

    async fetchParcels(coordinates: LngLat[], map: MapRef): Promise<IParcel[]> {
        const parcels = await this.parcelApiService.getParcelInfos(coordinates);

        await Promise.all(parcels.map(async parcel => {
            const coord = new LngLat(parcel.coordinate.longitude, parcel.coordinate.latitude);
            const mapboxPlace = await this.mapboxApiService.getMapboxParcelInfo(coord);
            const county = this.parcelApiService.getCountyName(map, coord);
            const municipality = this.parcelApiService.getMunicipalityName(map, coord);

            parcel.streetAddress = mapboxPlace.address;
            parcel.city = mapboxPlace.place;
            parcel.county = county;
            parcel.municipality = municipality;
            parcel.state = mapboxPlace.region;
            parcel.zipCode = parseInt(mapboxPlace.postcode) || 0;

            if (!parcel.zoningType)
                parcel.zoningType = this.parcelApiService.getZoningType(map, county, coord) || this.parcelApiService.getZoningNameByPoint(map, coord);

            this.parcels.set(parcel.id, parcel);
        }));

        return parcels;
    }

    private findParcel(coordinate: LngLat): IParcel | undefined {
        const point = { latitude: coordinate.lat, longitude: coordinate.lng };
        return Array.from(this.parcels.values()).find(p => this.geometryService.isPointInsideShape(point, p.shape));
    }

    private propertyKey(coordinate: LngLat): string {
        return `${coordinate.lng}:${coordinate.lat}`;
    }
}
